import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalOffer as LocalOfferIcon,
  Inventory as InventoryIcon,
  MailOutline as MailOutlineIcon,
  Home as HomeIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  AdminPanelSettings as AdminIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const { getCartItemCount } = useCart();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const location = useLocation();

  // Determine text color based on current mode
  const textColor = mode === 'light' ? '#000000' : '#FFFFFF';
  const contrastColor = mode === 'light' ? '#000000' : '#FFFFFF';

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleLogout = () => {
    logout();
    navigate('/');
  };



  const handleLogin = () => {
    navigate('/profile');
  };

  // Helper function to navigate and close drawer
  const navigateTo = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box>
      <List>
        <ListItem component="div" onClick={() => navigateTo('/')} sx={{ cursor: 'pointer', py: 2, bgcolor: 'secondary.main' }}>
          <ListItemText 
            primary="Homepage" 
            primaryTypographyProps={{ 
              variant: 'h6', 
              fontWeight: 'bold',
              color: mode === 'light' ? '#FFFFFF' : '#000000', // Contrast color for the header
              textAlign: 'center'
            }} 
          />
        </ListItem>
        
        <Divider />
        
        <ListItem component="div" onClick={() => navigateTo('/')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon><HomeIcon /></ListItemIcon>
          <ListItemText primary="Home" />
        </ListItem>

        <ListItem component="div" onClick={() => navigateTo('/cards')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon><InventoryIcon /></ListItemIcon>
          <ListItemText primary="Goat Collection" />
        </ListItem>

        <ListItem component="div" onClick={() => navigateTo('/ebay-listings')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon><LocalOfferIcon /></ListItemIcon>
          <ListItemText primary="Live Listings" />
        </ListItem>
        <ListItem component="div" onClick={() => navigateTo('/boxes')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon><InventoryIcon /></ListItemIcon>
          <ListItemText primary="Buy Boxes" />
        </ListItem>
        <ListItem component="div" onClick={() => navigateTo('/signup')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon><MailOutlineIcon /></ListItemIcon>
          <ListItemText primary="Signup for Emails" />
        </ListItem>
        {user && (
          <ListItem component="div" onClick={() => navigateTo('/profile')} sx={{ cursor: 'pointer' }}>
            <ListItemIcon><PersonIcon /></ListItemIcon>
            <ListItemText primary="Profile" />
          </ListItem>
        )}
        
        {/* Admin section */}
        {isAdmin && (
          <>
            <Divider />
            <ListItem component="div" sx={{ py: 1 }}>
              <Chip 
                label="ADMIN" 
                color="error" 
                size="small" 
                sx={{ fontWeight: 'bold' }}
              />
            </ListItem>
            <ListItem component="div" onClick={() => navigateTo('/admin-yyd92c')} sx={{ cursor: 'pointer' }}>
              <ListItemIcon><AdminIcon /></ListItemIcon>
              <ListItemText primary="Admin Login" />
            </ListItem>
            <ListItem component="div" onClick={() => navigateTo('/management-portal/dashboard')} sx={{ cursor: 'pointer' }}>
              <ListItemIcon><InventoryIcon /></ListItemIcon>
              <ListItemText primary="Admin Dashboard" />
            </ListItem>
          </>
        )}
        
        <Divider />
        
        {/* Theme Toggle in Drawer */}
        <ListItem component="div" onClick={toggleTheme} sx={{ cursor: 'pointer' }}>
          <ListItemIcon>
            {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
          </ListItemIcon>
          <ListItemText primary={`Switch to ${mode === 'light' ? 'Dark' : 'Light'} Mode`} />
        </ListItem>
        
        <Divider />
        
        {/* About Us */}
        <ListItem component="div" onClick={() => navigateTo('/about')} sx={{ cursor: 'pointer' }}>
          <ListItemIcon><InfoIcon /></ListItemIcon>
          <ListItemText primary="About Us" />
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: mode === 'light' ? '#FFFFFF' : '#000000',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          color: textColor,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <Toolbar>
          {/* Hamburger menu now visible on all screen sizes and placed before the logo */}
          <IconButton
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: { xs: 0.25, sm: 0.5, md: 2 }, 
              color: contrastColor,
              padding: { xs: '2px', sm: '4px', md: '8px' },
              minWidth: { xs: '28px', sm: '32px', md: '48px' },
              minHeight: { xs: '28px', sm: '32px', md: '48px' }
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Typography
            variant="h4"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              color: contrastColor,
              cursor: 'pointer',
              letterSpacing: '-0.025em',
              display: 'flex',
              alignItems: 'center',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              padding: 0,
              margin: 0,
            }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src={mode === 'light' ? '/goat-cards-logo.jpg' : '/goat-cards-logo-black.png'}
              alt="Logo"
              sx={{
                width: { xs: '40px', sm: '50px', md: '80px' },
                height: { xs: '40px', sm: '50px', md: '80px' },
                background: mode === 'dark' ? '#1a1a1a' : 'transparent',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                borderRadius: '0',
                objectFit: 'contain'
              }}
            />
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.25, sm: 0.5, md: 2 },
            flexWrap: 'nowrap',
            minWidth: 0
          }}>
            {/* Cart button */}
            <Tooltip title="Shopping Cart">
              <IconButton 
                onClick={() => navigate('/cart')} 
                sx={{ 
                  color: contrastColor, 
                  position: 'relative',
                  padding: { xs: '2px', sm: '4px', md: '8px' },
                  minWidth: { xs: '28px', sm: '32px', md: '48px' },
                  minHeight: { xs: '28px', sm: '32px', md: '48px' }
                }}
              >
                <ShoppingCartIcon />
                {getCartItemCount() > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'error.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 20,
                      height: 20,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      border: '2px solid',
                      borderColor: mode === 'light' ? '#FFFFFF' : '#000000',
                    }}
                  >
                    {getCartItemCount() > 99 ? '99+' : getCartItemCount()}
                  </Box>
                )}
              </IconButton>
            </Tooltip>

            {/* Theme toggle button */}
            <Tooltip title={`Switch to ${mode === 'light' ? 'Dark' : 'Light'} Mode`}>
              <IconButton 
                onClick={toggleTheme} 
                sx={{ 
                  color: contrastColor,
                  padding: { xs: '2px', sm: '4px', md: '8px' },
                  minWidth: { xs: '28px', sm: '32px', md: '48px' },
                  minHeight: { xs: '28px', sm: '32px', md: '48px' }
                }}
              >
                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Tooltip>

            {user ? (
              <>
                <IconButton
                  onClick={() => navigate('/profile')}
                  sx={{ 
                    color: contrastColor,
                    padding: { xs: '2px', sm: '4px', md: '8px' },
                    minWidth: { xs: '28px', sm: '32px', md: '48px' },
                    minHeight: { xs: '28px', sm: '32px', md: '48px' }
                  }}
                >
                  <FavoriteIcon />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.5 } }}>
                  {isAdmin && (
                    <Tooltip title="Admin Access">
                      <Chip 
                        label="ADMIN" 
                        color="error" 
                        size="small" 
                        sx={{ 
                          fontWeight: 'bold', 
                          fontSize: { xs: '0.55rem', sm: '0.6rem' },
                          height: { xs: '18px', sm: '20px' },
                          '& .MuiChip-label': {
                            padding: { xs: '0 4px', sm: '0 6px' }
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                  <IconButton
                    onClick={() => navigate('/profile')}
                    sx={{ 
                      padding: { xs: '1px', sm: '2px', md: '8px' },
                      minWidth: { xs: '24px', sm: '28px', md: '40px' },
                      minHeight: { xs: '24px', sm: '28px', md: '40px' }
                    }}
                  >
                    <Avatar sx={{ 
                      width: { xs: 24, sm: 28, md: 32 }, 
                      height: { xs: 24, sm: 28, md: 32 }, 
                      bgcolor: mode === 'light' ? '#000000' : '#FFFFFF', 
                      color: mode === 'light' ? '#FFFFFF' : '#000000' 
                    }}>
                      <PersonIcon />
                    </Avatar>
                  </IconButton>
                </Box>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleLogin}
                sx={{ 
                  bgcolor: mode === 'light' ? '#000000' : '#FFFFFF',
                  color: mode === 'light' ? '#FFFFFF' : '#000000',
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.875rem' },
                  padding: { xs: '2px 6px', sm: '4px 8px', md: '8px 16px' },
                  minWidth: { xs: 'auto', sm: 'auto', md: '64px' },
                  minHeight: { xs: '24px', sm: '28px', md: '36px' },
                  '&:hover': {
                    bgcolor: mode === 'light' ? '#333333' : '#E0E0E0',
                  }
                }}
              >
                Sign In
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
      {/* Add toolbar placeholder to prevent content from hiding under the appbar */}
      <Toolbar />
    </>
  );
};

export default Header; 